/** Entrypoint `pos-web`. Peran = shell berbeda di atas inti yang sama (K4). */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './i18n';
import './styles.css';
import { applyTheme, storedTheme } from './components/ThemeSwitcher';

/*
 * ⚠️ Tema dipasang SEBELUM React merender. Kalau menunggu efek komponen, layar berkedip putih
 * sepersekian detik tiap muat — dan di ruang remang, kedipan putih itu menyilaukan.
 */
applyTheme(storedTheme());
import { App } from './App';
import { requestPersistentStorage } from './api/cache';
import type { MenuItem } from './api/client';

/**
 * Menu dimuat dari **endpoint katalog**; `window.__MENU__` hanya jalur suntik untuk uji.
 * ⚠️ Bila katalog gagal dimuat, layar tetap **kosong dengan pesan** — menu karangan di layar kasir
 * lebih berbahaya daripada layar kosong.
 */
const menu: readonly MenuItem[] =
  (window as { __MENU__?: readonly MenuItem[] }).__MENU__ ?? [];

/**
 * Outlet perangkat ini. Di terminal sungguhan ia datang dari `device-agent` bersama tokennya;
 * saat pengembangan ia disimpan di `localStorage` oleh `dev-login-setup`.
 *
 * ⛔ Bukan dipahat di bundel: satu bundel dipakai SEMUA outlet, dan kode yang dipahat akan salah
 * di outlet berikutnya.
 */
const outletCode = localStorage.getItem('osbpos.outlet') ?? 'SMB';

/*
 * ⛔⛔ **`?role=` hanya berlaku saat PENGEMBANGAN.**
 *
 * Di produksi bentuk layar datang dari SERVER (`GET /devices/me`, dari `sub` token) — K4:
 * *"yang menentukan bentuk layar adalah PERANGKATnya, bukan orangnya"*. Selama `?role=` masih
 * dihormati di produksi, siapa pun yang bisa mengetik URL bisa mengubah tablet dapur jadi terminal
 * kasir, dan `device.role` yang disetel manajer di portal tak menentukan apa pun.
 *
 * ⚠️ Tetap dipertahankan di dev karena satu tablet dipakai menguji empat shell bergantian, dan
 * mendaftarkan empat perangkat untuk itu adalah friksi yang tak membeli apa pun.
 */
const devRoleParam = import.meta.env.DEV
  ? new URLSearchParams(window.location.search).get('role')
  : null;
if (devRoleParam !== null) localStorage.setItem('osbpos.role', devRoleParam);
const savedRole = import.meta.env.DEV ? localStorage.getItem('osbpos.role') : null;
const role =
  savedRole === 'kds' || savedRole === 'queue' || savedRole === 'waiter' || savedRole === 'cashier'
    ? savedRole
    : undefined;

/*
 * ⭐ Diminta SEBELUM merender: antrean `outbox` berisi penjualan yang uangnya sudah diterima, dan
 * penyimpanan best-effort boleh diusir peramban saat disk menipis. ⛔ Hasilnya sengaja diabaikan —
 * peramban lama tak punya API-nya, dan menolak jualan karena itu jauh lebih mahal.
 */
void requestPersistentStorage();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      ⛔ Tak ada `tenantCode` — ia datang dari token perangkat (R-AI-1), bukan dari berkas ini.

      ⛔⛔ Tak ada `businessDate` juga. Dulu berkas ini mengisinya dengan
      `new Date().toISOString().slice(0,10)` — tanggal **UTC**, yang di UTC+7 meleset satu hari
      setiap pukul 00:00–07:00 waktu setempat. Tanggal usaha adalah `day_cutover_time` + zona
      OUTLET; perangkat tak memegang keduanya, jadi ia memang tak berhak menjawabnya. `App`
      menanyakannya ke server.
    */}
    <App
      outletCode={outletCode}
      {...(role === undefined ? {} : { role })}
      deviceCode={localStorage.getItem('osbpos.device') ?? 'POS-DEV-01'}
      shiftId={(window as { __SHIFT__?: string }).__SHIFT__ ?? null}
      menu={menu}
    />
  </StrictMode>,
);
